/**
 * EtatRepasAgentsPanel — item 12.5, la transmission (arbitrage F).
 *
 * « C'est transmis à la comptabilité pour le nombre de repas à défalquer du
 *  salaire, ou à la RH, ou les deux. »
 *
 * Deux états coexistent, et c'est voulu :
 *  · **l'état vivant** du mois en cours, qui bouge à chaque pointage ;
 *  · **les états transmis**, FIGÉS au moment où ils sont partis en paie.
 *    Corriger un pointage de septembre en novembre ne réécrit pas ce qui a
 *    déjà été retenu sur un bulletin — les corrections vont au mois suivant.
 *
 * Le même panneau sert au scolaire (qui transmet) et à la compta / RH (qui
 * lisent). `peutTransmettre` fait la différence.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiException, type CantineEtatAdultes, type CantineEtatTransmis } from '@/lib/api';
import { Card, Button, Badge, Alert } from '@/components/ui';
import { AlertCircle, Check, Download, Send } from 'lucide-react';

const LIB: Record<string, string> = {
  agent: 'Agent', enseignant: 'Enseignant', elu: 'Élu', exterieur: 'Extérieur',
};

const euros = (c: number) => `${(c / 100).toFixed(2).replace('.', ',')} €`;

function moisPrecedent(): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 7);
}

function moisFr(periode: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris', month: 'long', year: 'numeric',
  }).format(new Date(`${periode}-01T12:00:00`));
}

interface Props {
  /** Le scolaire transmet ; la compta et la RH ne font que lire. */
  peutTransmettre?: boolean;
  /** Base d'URL de téléchargement selon la brique qui affiche le panneau. */
  baseTelechargement?: '/api/scolaire/cantine/adultes/etat' | '/api/comptabilite/repas-agents' | '/api/rh/repas-agents';
}

export function EtatRepasAgentsPanel({
  peutTransmettre = false,
  baseTelechargement = '/api/scolaire/cantine/adultes/etat',
}: Props) {
  const [periode, setPeriode] = useState(moisPrecedent());
  const [vivant, setVivant] = useState<CantineEtatAdultes | null>(null);
  const [transmis, setTransmis] = useState<CantineEtatTransmis[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const charger = useCallback(async () => {
    try {
      const t = await api.scolaire.adultes.transmissions();
      setTransmis(t.etats);
    } catch { setTransmis([]); }
    if (peutTransmettre) {
      try { setVivant(await api.scolaire.adultes.etatMensuel(periode)); }
      catch { setVivant(null); }
    }
  }, [periode, peutTransmettre]);

  useEffect(() => { void charger(); }, [charger]);

  const dejaTransmis = transmis.find((e) => e.periode === periode);

  async function agir(fn: () => Promise<unknown>, ok: string) {
    setBusy(true); setError(null); setMsg(null);
    try { await fn(); setMsg(ok); await charger(); }
    catch (e) { setError(e instanceof ApiException ? e.payload.error : 'Action impossible'); }
    finally { setBusy(false); }
  }

  function telecharger(p: string) {
    const url = baseTelechargement.includes('repas-agents')
      ? `${baseTelechargement}/${p}/xlsx`
      : `${baseTelechargement}/${p}/xlsx`;
    window.open(url, '_blank', 'noopener');
  }

  return (
    <Card variant="glass" padded>
      <h3 className="mb-1 text-sm font-medium text-ink">Repas des agents — état mensuel</h3>
      <p className="mb-3 text-xs text-ink-muted">
        {peutTransmettre
          ? 'Le mois transmis est figé : il ne bougera plus, même si un pointage est corrigé ensuite. Les corrections vont au mois suivant.'
          : 'États transmis par le service scolaire, à reprendre pour la retenue sur salaire. Chaque état est figé au moment où il a été transmis.'}
      </p>

      {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
      {msg && <Alert variant="success" icon={Check}>{msg}</Alert>}

      {peutTransmettre && (
        <div className="mb-3 rounded-r-lg border border-border/60 p-2.5">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <label className="text-sm text-ink">
              Mois :{' '}
              <input type="month" value={periode} onChange={(e) => setPeriode(e.target.value)}
                aria-label="Mois de l'état"
                className="h-9 rounded-r-md border border-border bg-surface/60 px-2.5 text-sm text-ink focus:border-accent focus:outline-none" />
            </label>
            {dejaTransmis
              ? <Badge variant="success" size="sm">Déjà transmis</Badge>
              : <Badge variant="warning" size="sm">Non transmis</Badge>}
          </div>

          {vivant && vivant.totalRepas > 0 ? (
            <>
              <p className="text-sm text-ink">
                <strong>{vivant.totalRepas}</strong> repas · <strong>{euros(vivant.totalCentimes)}</strong>
                {' '}· {vivant.lignes.length} personne{vivant.lignes.length > 1 ? 's' : ''}
              </p>
              <ul className="mt-1 space-y-0.5">
                {vivant.lignes.map((l) => (
                  <li key={l.conviveId} className="text-xs text-ink-muted">
                    {l.nomComplet} ({LIB[l.categorie] ?? l.categorie}) — {l.nbRepas} repas · {euros(l.montantCentimes)}
                  </li>
                ))}
              </ul>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {dejaTransmis ? (
                  <Button variant="ghost" size="sm" disabled={busy}
                    onClick={() => void agir(() => api.scolaire.adultes.retirerTransmission(periode),
                      'Transmission retirée. Vous pouvez en envoyer une nouvelle.')}>
                    Retirer la transmission
                  </Button>
                ) : (
                  <Button variant="primary" size="sm" disabled={busy}
                    onClick={() => void agir(() => api.scolaire.adultes.transmettre(periode),
                      'État transmis — la comptabilité et la RH sont prévenues.')}>
                    <Send className="h-4 w-4" aria-hidden="true" /> Transmettre à la compta et à la RH
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => telecharger(periode)}>
                  <Download className="h-4 w-4" aria-hidden="true" /> Excel
                </Button>
              </div>
            </>
          ) : (
            <p className="text-sm text-ink-muted">Aucun repas sur ce mois.</p>
          )}
        </div>
      )}

      <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-muted">
        Historique des transmissions
      </h4>
      {transmis.length === 0 ? (
        <p className="text-sm text-ink-muted">Aucun état transmis pour l'instant.</p>
      ) : (
        <ul className="space-y-1">
          {transmis.map((e) => (
            <li key={e.id} className="flex flex-wrap items-center gap-2 rounded-r-md border border-border/60 px-2.5 py-1.5">
              <span className="text-sm font-medium text-ink">{moisFr(e.periode)}</span>
              <span className="text-sm text-ink">{e.totalRepas} repas · {euros(e.totalCentimes)}</span>
              <span className="text-xs text-ink-muted">
                transmis le {new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', dateStyle: 'short' }).format(new Date(e.transmisAt))}
                {e.transmisParNom ? ` par ${e.transmisParNom}` : ''}
              </span>
              <Button variant="ghost" size="sm" className="ml-auto" onClick={() => telecharger(e.periode)}>
                <Download className="h-4 w-4" aria-hidden="true" /> Excel
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
